import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { DateInput } from "@/components/ui/date-input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { NumberInput } from "@/components/ui/number-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  type ManualTransaction,
  SCHEDULE_E_CATEGORIES,
  useAccounting,
} from "@/hooks/use-accounting";

const PORTFOLIO = "__portfolio__";

const schema = z.object({
  transaction_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date is required"),
  type: z.enum(["expense", "income"]),
  pipeline_property_id: z.string(),
  category: z.string().min(1),
  amount: z.coerce.number().positive("Enter a positive amount"),
  description: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  year: number;
  /** When set, the dialog edits this manual transaction; otherwise it creates a new one. */
  editing: ManualTransaction | null;
}

const defaultCategory = (type: "income" | "expense") => (type === "income" ? "rents_received" : "other");

export function TransactionFormDialog({ open, onOpenChange, year, editing }: Props) {
  const { properties, createManualTransaction, updateManualTransaction } = useAccounting(year);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      transaction_date: `${year}-01-01`,
      type: "expense",
      pipeline_property_id: PORTFOLIO,
      category: "other",
      amount: 0,
      description: "",
    },
  });

  const type = form.watch("type");

  // Hydrate the form whenever the dialog opens (new or editing).
  useEffect(() => {
    if (!open) return;
    if (editing) {
      form.reset({
        transaction_date: editing.transaction_date,
        type: editing.type === "income" ? "income" : "expense",
        pipeline_property_id: editing.pipeline_property_id ?? PORTFOLIO,
        category: editing.category,
        amount: Number(editing.amount),
        description: editing.description ?? "",
      });
    } else {
      form.reset({
        transaction_date: `${year}-01-01`,
        type: "expense",
        pipeline_property_id: PORTFOLIO,
        category: "other",
        amount: 0,
        description: "",
      });
    }
  }, [open, editing, year, form]);

  const categories = SCHEDULE_E_CATEGORIES.filter((c) => c.type === type);

  const handleTypeChange = (value: "income" | "expense") => {
    form.setValue("type", value);
    const stillValid = SCHEDULE_E_CATEGORIES.some((c) => c.type === value && c.key === form.getValues("category"));
    if (!stillValid) form.setValue("category", defaultCategory(value));
  };

  const onSubmit = (values: FormValues) => {
    const payload = {
      transaction_date: values.transaction_date,
      amount: values.amount,
      type: values.type,
      category: values.category,
      description: values.description?.trim() || null,
      pipeline_property_id: values.pipeline_property_id === PORTFOLIO ? null : values.pipeline_property_id,
    };
    if (editing) {
      updateManualTransaction.mutate(
        { id: editing.id, ...payload },
        { onSuccess: () => onOpenChange(false) },
      );
    } else {
      createManualTransaction.mutate(
        { ...payload, vendor: null, is_tax_deductible: true, notes: null },
        { onSuccess: () => onOpenChange(false) },
      );
    }
  };

  const isPending = createManualTransaction.isPending || updateManualTransaction.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit transaction" : "Add transaction"}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3.5">
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="transaction_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date</FormLabel>
                    <FormControl>
                      <DateInput {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type</FormLabel>
                    <Select value={field.value} onValueChange={(v) => handleTypeChange(v as "income" | "expense")}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="expense">Expense</SelectItem>
                        <SelectItem value="income">Income</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="pipeline_property_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Property</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={PORTFOLIO}>Portfolio-wide</SelectItem>
                      {properties.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category (Schedule E line)</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c.key} value={c.key}>Line {c.line} · {c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount</FormLabel>
                  <FormControl>
                    <NumberInput
                      step="0.01"
                      placeholder="0.00"
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea rows={2} placeholder="e.g., Check to Mike's Plumbing for sink repair" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {editing ? "Save changes" : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
